import { inject, Injectable } from '@angular/core'

import { FirebaseAuthentication } from '@capacitor-firebase/authentication'
import { FirebaseFirestore, QueryFilterConstraint, Timestamp } from '@capacitor-firebase/firestore'
import { combineLatest, filter, firstValueFrom, Observable, ReplaySubject } from 'rxjs'

import { RealtimeEvent, RealtimeEventType } from '@ti/contracts'
import { ApiService } from '@ti/core/api'
import { AuthSession } from '@ti/core/auth/services'
import { LOGIN_AS_SESSION } from '@ti/core/common/contracts'
import { injectDestroy$ } from '@ti/core/common/utils'
import { FIREBASE_APP } from '@ti/core/contracts/firebase'
import { NetworkStatusProvider } from '@ti/core/contracts/network'

import { REALTIME_TOKEN_FETCHER } from '../contracts'

@Injectable({ providedIn: 'root' })
export class RealtimeClient {
  public readonly initialized$: Observable<boolean>

  protected readonly api = inject(ApiService)
  protected readonly authSession = inject(AuthSession)
  protected readonly fbAppPromise = inject(FIREBASE_APP, { optional: true })
  protected readonly network = inject(NetworkStatusProvider)
  protected readonly loginAsSession = inject(LOGIN_AS_SESSION, { optional: true })
  protected readonly tokenFetcher = inject(REALTIME_TOKEN_FETCHER, { optional: true })
  protected readonly destroy$ = injectDestroy$()

  protected readonly initializedSubject = new ReplaySubject<boolean>(1)

  protected _initializedPromise?: Promise<boolean>
  protected _isStoreInitialized?: boolean

  public constructor() {
    this.initialized$ = this.initializedSubject.asObservable()

    this.ensureFirebaseInitialized().catch((e) =>
      console.error('Cannot initialize RealtimeClient', e),
    )
  }

  // TODO: Move firebase initialization away from RealtimeClient
  async ensureFirebaseInitialized() {
    if (this._isStoreInitialized) {
      return true
    }

    if (!this._initializedPromise) {
      this._initializedPromise = (async () => {
        if (!this._isStoreInitialized) {
          await this.fbAppPromise

          // Wait until the user is authenticated
          await firstValueFrom(this.authSession.state.isAuthenticated$.pipe(filter(Boolean)))

          // Suspend initialization until the network is available
          await firstValueFrom(this.network.isConnected$.pipe(filter(Boolean)))

          const token = await this.fetchRealtimeToken()
          await FirebaseAuthentication.signInWithCustomToken({ token })
          this._isStoreInitialized = true
          this.initializedSubject.next(true)
        }
        delete this._initializedPromise
        return this._isStoreInitialized
      })()
    }

    return this._initializedPromise
  }

  async subscribeToEvents(
    eventType: RealtimeEventType,
    handler: (event: RealtimeEvent) => void,
  ): Promise<() => void>
  async subscribeToEvents(
    eventTypes: readonly RealtimeEventType[],
    handler: (event: RealtimeEvent) => void,
  ): Promise<() => void>
  async subscribeToEvents(
    eventTypeOrTypes: RealtimeEventType | readonly RealtimeEventType[],
    handler: (event: RealtimeEvent) => void,
  ): Promise<() => void> {
    let callbackId: string | undefined
    const subscription = combineLatest([
      this.authSession.state.currentUserAttributes$,
      this.initialized$,
    ]).subscribe(async ([currentUserAttrs]) => {
      if (!currentUserAttrs) {
        if (callbackId) {
          console.debug('REALTIME Unsubscribing updates listener')
          await FirebaseFirestore.removeSnapshotListener({ callbackId })
          callbackId = undefined
        }
        return
      }

      if (callbackId) {
        await FirebaseFirestore.removeSnapshotListener({ callbackId })
      }

      const filterConstraints: QueryFilterConstraint[] = [
        {
          type: 'where',
          fieldPath: 'createdAt',
          opStr: '>',
          value: Timestamp.fromDate(new Date()),
        },
      ]

      if (Array.isArray(eventTypeOrTypes)) {
        filterConstraints.push({
          type: 'where',
          fieldPath: 'type',
          opStr: 'in',
          value: eventTypeOrTypes,
        })
      } else {
        filterConstraints.push({
          type: 'where',
          fieldPath: 'type',
          opStr: '==',
          value: eventTypeOrTypes,
        })
      }

      callbackId = await FirebaseFirestore.addCollectionSnapshotListener(
        {
          reference: this.getEventsCollectionName(
            this.loginAsSession?.targetUserId ?? currentUserAttrs.sub,
          ),
          compositeFilter: {
            type: 'and',
            queryConstraints: filterConstraints,
          },
          queryConstraints: [
            {
              type: 'orderBy',
              fieldPath: 'createdAt',
              directionStr: 'desc',
            },
            {
              type: 'limit',
              limit: 1,
            },
          ],
        },
        (event, error) => {
          if (error) {
            console.error('REALTIME Snapshot listener error', error)
            return
          }
          if (event) {
            const snapshots = event.snapshots
            if (snapshots && snapshots.length > 0) {
              handler(snapshots[0].data as RealtimeEvent)
            }
          }
        },
      )
    })

    const destroySub = this.destroy$.subscribe(() => {
      subscription.unsubscribe()
      if (callbackId) {
        FirebaseFirestore.removeSnapshotListener({ callbackId })
      }
    })

    return () => {
      subscription.unsubscribe()
      destroySub.unsubscribe()
      if (callbackId) {
        FirebaseFirestore.removeSnapshotListener({ callbackId })
      }
    }
  }

  protected getEventsCollectionName(userId: string) {
    return `realtime/${userId}/events`
  }

  protected fetchRealtimeToken() {
    // Defaults to the Fleet endpoint; the TSP and TI apps override it because
    // their APIs expose the route under their own user scope.
    return this.tokenFetcher ? this.tokenFetcher() : this.api.fleet.user.getRealtimeToken()
  }
}
